// Copy for the B+ signal page. `{name}` placeholders are filled by fmt().
const translations = {
    en: {
        kicker: 'Nasdaq-100 strategy signal',
        title: 'QQQ three-layer strategy · {version} daily signal',
        subtitle:
            'A mean-reversion strategy on QQQ with moving-average filters: it adds TQQQ on '
            + 'bull-market pullbacks and after deep drawdowns, and cuts the QQQ base in bear '
            + 'markets. Prices come from yfinance and refresh after every U.S. market close.',
        asOf: 'Data as of the {date} close',
        version: 'Version',
        language: 'Language',
        loading: 'Loading today’s signal…',
        error: 'Could not load the signal. The FastAPI service may be down; try again in a minute.',
        retry: 'Try again',
        locked: {
            title: 'This page needs a password',
            body: 'The B+ signal is private. Enter the password once and this browser will remember it.',
            relock: 'Lock',
        },
        unavailable: 'Not enough price history yet. The first refresh downloads it; check back shortly.',

        signal: {
            heading: 'Today’s signal',
            target: 'Target allocation',
            exposure: '≈ {x}× QQQ exposure',
            since: 'Since {date} (QQQ {price})',
            todayNone: 'No new signal at today’s close.',
            todayNew: 'New at today’s close',
            close: 'QQQ close',
            drawdown: 'From 52-week high',
            ext200: 'Above / below SMA200',
            assets: { qqq: 'QQQ', tqqq: 'TQQQ', cash: 'Cash' },
        },
        states: {
            pullback: { name: '② Bull-market pullback · holding TQQQ', desc: 'QQQ pulled back below EMA100 while above a rising SMA200, so part of the portfolio is in TQQQ.' },
            deep_rebound: { name: '① Deep-drawdown reversal · holding TQQQ', desc: 'After a 20%+ drawdown, QQQ closed back above a rising SMA50, so part of the portfolio is in TQQQ.' },
            deep_watch: { name: '① Deep drawdown · waiting for confirmation', desc: 'QQQ is 20%+ below its 52-week high. TQQQ is bought only after a close above a rising SMA50.' },
            bear: { name: '③ Bear filter · base reduced', desc: 'QQQ closed more than {buf} below a falling SMA200, so the QQQ base is cut to {base}.' },
            normal: { name: 'Normal · 100% QQQ', desc: 'No layer is active. The portfolio simply holds QQQ.' },
        },

        next: {
            heading: 'What would change the position next',
            note: 'At today’s indicator levels. Moving averages shift a little every day, so these prices move too. Signals use closing prices only.',
            below: 'Close below',
            above: 'Close above',
            codes: {
                pb_stop: '② Stop: sell the TQQQ when QQQ closes below SMA200',
                pb_exit_ext: '② Take profit: sell the TQQQ when QQQ closes {ext} above SMA200',
                pb_exit_high: '② Take profit: sell the TQQQ at a new 52-week closing high',
                pb_entry: '② Add {w} TQQQ when QQQ closes below EMA100',
                pb_wait_bull: '② Pullback buys resume once QQQ closes above a rising SMA200',
                deep_buy: '① Buy TQQQ when QQQ closes above SMA50 and SMA50 is rising',
                deep_stop: '① Stop: TQQQ goes to cash on a close below SMA50',
                deep_exit: '① Take profit: TQQQ back to QQQ near the 52-week high',
                deep_arm: '① Arms when QQQ closes {dd} below its 52-week high',
                bear_on: '③ Bear filter: QQQ base to {base} on a close {buf} below a falling SMA200',
                bear_off: '③ Bear filter lifts on a close back above SMA200 (or when SMA200 turns up)',
            },
            notes: {
                sma200_rising: 'SMA200 is still rising, so this cannot trigger yet.',
                sma200_not_rising: 'SMA200 also has to turn up.',
                sma50_not_rising: 'SMA50 also has to turn up.',
            },
        },

        chart: {
            heading: 'Price, lines and position',
            note: 'Green shading: holding TQQQ. Red shading: part of the portfolio in cash.',
            ranges: { '6m': '6M', '1y': '1Y', '2y': '2Y', '3y': '3Y' },
            series: { close: 'QQQ close', ema_pb: 'EMA100 (pullback line)', sma50: 'SMA50', sma200: 'SMA200 (trend line)', bear_line: 'Bear trigger', exit_line: '② Take-profit line' },
            tqqq: 'TQQQ {w}',
            cash: 'Cash {w}',
        },

        levels: {
            heading: 'Key levels',
            level: 'Level', price: 'QQQ price', distance: 'From close',
            rows: {
                ema_pb: 'EMA100 · pullback line', sma50: 'SMA50 · confirm / stop line', sma200: 'SMA200 · trend line',
                bear_line: 'Bear trigger · SMA200 −{buf}', exit_line: '② Take-profit line · SMA200 +{ext}',
                hi252: '52-week closing high', arm_line: '① Arm line · −{dd} from high',
            },
        },

        events: {
            heading: 'Recent signals',
            date: 'Date', signal: 'Signal', close: 'QQQ close',
            codes: {
                arm: '① Armed: {v} below the 52-week high',
                deep_buy: '① Reversal: buy TQQQ {v}',
                deep_add: '① Add: TQQQ to {v}',
                deep_stop: '① Stop: TQQQ to cash',
                deep_exit: '① Take profit: TQQQ back to QQQ',
                pb_buy: '② Pullback: add TQQQ {v}',
                pb_stop: '② Stop: closed below SMA200',
                pb_exit_ext: '② Take profit: {ext} above SMA200',
                pb_exit_newhigh: '② Take profit: new 52-week high',
                bear_on: '③ Bear filter on: QQQ base to {v}',
                bear_off: '③ Bear filter off: base back to 100%',
            },
        },

        perf: {
            heading: 'Backtest since {start}',
            headingWindow: 'Backtest: last {n} years (since {start})',
            window: 'Backtest period',
            windows: { '3y': '3Y', '5y': '5Y', '10y': '10Y', all: 'All ({n}Y)' },
            note: 'Uses real TQQQ prices, trades at the signal day’s close, charges 0.05% per switch, cash earns the 13-week T-bill rate, no taxes. Past results do not predict future returns.',
            strategy: 'Strategy', cagr: 'Annualized', maxdd: 'Max drawdown', sharpe: 'Sharpe', ytd: 'This year', last12: 'Last 12 months', worst: 'Worst year', growth: '$1 grew to',
            names: { bplus: 'B+', b: 'B', qqq: 'QQQ buy & hold', tqqq: 'TQQQ buy & hold' },
            chartTitle: 'Growth of $1 (log scale)',
        },

        rules: {
            heading: 'The rules ({version})',
            items: [
                { title: '① Deep drawdown → TQQQ', body: 'Arm when QQQ closes {dd} below its 52-week high. Buy TQQQ when it closes above a rising SMA50: {w1} of the portfolio, {w2} once the drawdown has reached {dd2}. Stop on a close below SMA50 (TQQQ goes to cash). {deepExit}' },
                { title: '② Bull-market pullback → TQQQ', body: 'While QQQ is above a rising SMA200, a close below EMA100 moves {pb} of the portfolio into TQQQ. Stop on a close below SMA200. {pbExit}' },
                { title: '③ Bear filter', body: 'When QQQ closes more than {buf} below a falling SMA200, the QQQ base drops to {base} and the rest sits in cash. It lifts on a close back above SMA200 or when SMA200 turns up.' },
            ],
            deepExitNewHigh: 'Take profit when QQQ is back at its 52-week high.',
            deepExitNear: 'Take profit when QQQ is back within {x} of its 52-week high.',
            pbExitExt: 'Take profit when QQQ closes {ext} above SMA200.',
            pbExitNewHigh: 'Take profit at a new 52-week closing high.',
            other: 'With no layer active, the portfolio holds 100% QQQ. B+ and B differ only in when they take profit.',
        },

        disclaimer: 'Research tool, not investment advice. Signals are computed mechanically from historical rules; markets can behave differently from the backtest.',
        backToMonitor: 'QQQ SMA monitor',
        forwardPe: 'QQQ forward P/E',
    },

    zh: {
        kicker: '纳斯达克 100 策略信号',
        title: 'QQQ 三层策略 · {version} 每日信号',
        subtitle:
            '基于均线过滤的 QQQ 均值回归策略：牛市回调和深度下跌后加 TQQQ，熊市里降低 QQQ 底仓。'
            + '行情来自 yfinance，每个美股交易日收盘后自动更新。',
        asOf: '数据截至 {date} 收盘',
        version: '版本',
        language: '语言',
        loading: '正在读取今天的信号…',
        error: '信号读取失败，可能是 FastAPI 服务暂时不可用，请稍后再试。',
        retry: '重试',
        locked: {
            title: '这个页面需要密码',
            body: 'B+ 信号不公开。输入一次密码后，这个浏览器会记住它。',
            relock: '锁定',
        },
        unavailable: '历史行情还不够，首次刷新正在下载，请稍后再来。',

        signal: {
            heading: '今日信号',
            target: '目标仓位',
            exposure: '约等于 {x} 倍 QQQ',
            since: '自 {date} 起（QQQ {price}）',
            todayNone: '今天收盘没有新信号。',
            todayNew: '今天收盘的新信号',
            close: 'QQQ 收盘',
            drawdown: '距 52 周高点',
            ext200: '偏离 200 日线',
            assets: { qqq: 'QQQ', tqqq: 'TQQQ', cash: '现金' },
        },
        states: {
            pullback: { name: '② 牛市回调 · 持有 TQQQ', desc: 'QQQ 在上行的 200 日线上方回调到 EMA100 以下，部分仓位换成了 TQQQ。' },
            deep_rebound: { name: '① 深跌反转 · 持有 TQQQ', desc: '回撤超过 20% 之后，QQQ 收盘重新站上上行的 50 日线，部分仓位换成了 TQQQ。' },
            deep_watch: { name: '① 深度回撤 · 等待确认', desc: 'QQQ 距 52 周高点回撤超过 20%，要等收盘站上上行的 50 日线才买 TQQQ。' },
            bear: { name: '③ 熊市过滤 · 底仓已降', desc: 'QQQ 收盘跌破下行的 200 日线超过 {buf}，QQQ 底仓降到 {base}。' },
            normal: { name: '正常 · 100% 持有 QQQ', desc: '三层都没有触发，组合就是满仓 QQQ。' },
        },

        next: {
            heading: '接下来什么情况会改变仓位',
            note: '按今天的指标水平计算。均线每天都会小幅移动，这些价位也会跟着变。所有信号只看收盘价。',
            below: '收盘低于',
            above: '收盘高于',
            codes: {
                pb_stop: '② 止损：QQQ 收盘跌破 200 日线，卖出 TQQQ',
                pb_exit_ext: '② 止盈：QQQ 收盘高于 200 日线 {ext}，卖出 TQQQ',
                pb_exit_high: '② 止盈：QQQ 收盘创 52 周新高，卖出 TQQQ',
                pb_entry: '② QQQ 收盘跌破 EMA100，加 {w} TQQQ',
                pb_wait_bull: '② 要等 QQQ 收在上行的 200 日线上方，才会恢复回调加仓',
                deep_buy: '① QQQ 收盘站上 50 日线、且 50 日线上行时买入 TQQQ',
                deep_stop: '① 止损：收盘跌破 50 日线，TQQQ 换成现金',
                deep_exit: '① 止盈：回到 52 周高点附近，TQQQ 换回 QQQ',
                deep_arm: '① QQQ 收盘距 52 周高点回撤 {dd} 时上膛',
                bear_on: '③ 熊市降仓：收盘跌破下行的 200 日线 {buf}，QQQ 底仓降到 {base}',
                bear_off: '③ 熊市解除：收盘回到 200 日线上方（或 200 日线拐头向上）',
            },
            notes: {
                sma200_rising: '200 日线还在上行，暂时不会触发。',
                sma200_not_rising: '还需要 200 日线拐头向上。',
                sma50_not_rising: '还需要 50 日线拐头向上。',
            },
        },

        chart: {
            heading: '价格、均线和仓位',
            note: '绿色底色：持有 TQQQ；红色底色：部分仓位为现金。',
            ranges: { '6m': '6 个月', '1y': '1 年', '2y': '2 年', '3y': '3 年' },
            series: { close: 'QQQ 收盘', ema_pb: 'EMA100（回调线）', sma50: 'SMA50', sma200: 'SMA200（牛熊线）', bear_line: '熊市触发线', exit_line: '② 止盈线' },
            tqqq: 'TQQQ {w}',
            cash: '现金 {w}',
        },

        levels: {
            heading: '关键价位',
            level: '价位', price: 'QQQ 价格', distance: '离收盘',
            rows: {
                ema_pb: 'EMA100 · 回调线', sma50: 'SMA50 · 确认 / 止损线', sma200: 'SMA200 · 牛熊线',
                bear_line: '熊市触发线 · SMA200 −{buf}', exit_line: '② 止盈线 · SMA200 +{ext}',
                hi252: '52 周收盘高点', arm_line: '① 上膛线 · 距高点 −{dd}',
            },
        },

        events: {
            heading: '近期信号',
            date: '日期', signal: '信号', close: 'QQQ 收盘',
            codes: {
                arm: '① 上膛：距 52 周高点回撤 {v}',
                deep_buy: '① 深跌反转：买入 TQQQ {v}',
                deep_add: '① 加仓：TQQQ 加到 {v}',
                deep_stop: '① 止损：TQQQ 换成现金',
                deep_exit: '① 止盈：TQQQ 换回 QQQ',
                pb_buy: '② 牛市回调：加 TQQQ {v}',
                pb_stop: '② 止损：跌破 200 日线',
                pb_exit_ext: '② 止盈：高于 200 日线 {ext}',
                pb_exit_newhigh: '② 止盈：创 52 周新高',
                bear_on: '③ 熊市：QQQ 底仓降到 {v}',
                bear_off: '③ 熊市解除：底仓恢复 100%',
            },
        },

        perf: {
            heading: '{start} 以来的回测',
            headingWindow: '最近 {n} 年回测（{start} 起）',
            window: '回测区间',
            windows: { '3y': '3 年', '5y': '5 年', '10y': '10 年', all: '全部（{n} 年）' },
            note: '使用 TQQQ 真实价格，当天收盘成交，每次换仓扣 0.05%，现金按 13 周美债利率计息，不计税。历史表现不代表未来。',
            strategy: '策略', cagr: '年化', maxdd: '最大回撤', sharpe: '夏普', ytd: '今年', last12: '最近 12 个月', worst: '最差一年', growth: '1 美元变成',
            names: { bplus: 'B+', b: 'B', qqq: 'QQQ 持有', tqqq: 'TQQQ 持有' },
            chartTitle: '1 美元的增长（对数坐标）',
        },

        rules: {
            heading: '规则（{version}）',
            items: [
                { title: '① 深跌换 TQQQ', body: 'QQQ 收盘距 52 周高点回撤 {dd} 时上膛；收盘站上上行的 50 日线时买入 TQQQ，占组合 {w1}，回撤达到 {dd2} 时为 {w2}。收盘跌破 50 日线止损，TQQQ 换成现金。{deepExit}' },
                { title: '② 牛市回调加 TQQQ', body: 'QQQ 在上行的 200 日线上方时，收盘跌破 EMA100 就把组合的 {pb} 换成 TQQQ。收盘跌破 200 日线止损。{pbExit}' },
                { title: '③ 熊市降底仓', body: 'QQQ 收盘跌破下行的 200 日线超过 {buf} 时，QQQ 底仓降到 {base}，其余持有现金；收盘回到 200 日线上方或 200 日线拐头向上时恢复。' },
            ],
            deepExitNewHigh: '回到 52 周高点时止盈。',
            deepExitNear: '回到距 52 周高点 {x} 以内时止盈。',
            pbExitExt: '收盘高于 200 日线 {ext} 时止盈。',
            pbExitNewHigh: '收盘创 52 周新高时止盈。',
            other: '三层都没触发时，100% 持有 QQQ。B+ 和 B 只在止盈时机上不同。',
        },

        disclaimer: '这是研究工具，不是投资建议。信号按历史规则机械计算，实际行情可能和回测不同。',
        backToMonitor: 'QQQ 均线监控',
        forwardPe: 'QQQ 远期市盈率',
    },
};

export default translations;
